// Helper to detect 'All' date selection
// import { UserExtended } from "@/types";
// import { UserExtended } from "@/types";
export function isAllDateSelected(user: UserExtended | null): boolean {
  return !user?.selectedYear && !user?.selectedMonth && !user?.selectedQuarter;
}

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { User, Company, BankAccount, BankInstitution } from "@prisma/client";
import {
  UserExtended,
  AccountExtended,
  StatementExtended,
  InvoiceExtended,
  TransactionExtended,
} from "@/types";

export interface StatementUploadResponse {
  ok?: boolean;
  data?: unknown;
  error?: unknown;
}

export interface InvoiceUploadResponse {
  eventIds?: string[];
  error?: unknown;
}

// Import slices
import createUserSlice from "./slices/userSlice";
import createCompaniesSlice from "./slices/companiesSlice";
import createBankAccountsSlice from "./slices/bankAccountsSlice";
import createInstitutionsSlice from "./slices/institutionsSlice";
import createTransactionsSlice from "./slices/transactionsSlice";
import createUploadSlice from "./slices/uploadSlice";
import createInvoiceSlice from "./slices/invoiceSlice";
import createStatementsSlice from "./slices/statementsSlice";

export interface AppStoreState {
  loading?: { [key: string]: boolean };
  statements: StatementExtended[];
  selectedAccount?: AccountExtended;
  selectedStatement?: StatementExtended;
  setStatements: (statements: StatementExtended[]) => void;
  setAccounts: (accounts: AccountExtended[]) => void;
  setSelectedAccount: (account: AccountExtended | undefined) => void;
  setSelectedStatement: (statement: StatementExtended | undefined) => void;
  fetchStatements: () => Promise<void>;
  fetchAccounts: () => Promise<void>;
  removeStatement: (statementId: string) => Promise<void>;
  user: UserExtended | null;
  setUser: (
    user:
      | UserExtended
      | null
      | ((prev: UserExtended | null) => UserExtended | null)
  ) => void;
  showAllData: () => Promise<void>;
  companies: Company[];
  setCompanies: (companies: Company[]) => void;
  selectedCompany: Company | null;
  setSelectedCompany: (company: Company | null) => void;
  bankAccounts: AccountExtended[];
  setBankAccounts: (accounts: AccountExtended[]) => void;
  institutions: BankInstitution[];
  setInstitutions: (institutions: BankInstitution[]) => void;
  uploadDocuments: (
    files: File[],
    uploadPath: string
  ) => Promise<StatementUploadResponse>;
  invoices: InvoiceExtended[];
  selectedInvoice?: InvoiceExtended;
  setInvoices: (invoices: InvoiceExtended[]) => void;
  setSelectedInvoice: (invoice: InvoiceExtended | undefined) => void;
  fetchInvoices: () => Promise<void>;
  uploadInvoice: (files: File[]) => Promise<InvoiceUploadResponse>;
  removeInvoice: (invoiceId: string) => Promise<void>;
  transactions: TransactionExtended[];
  setTransactions: (transactions: TransactionExtended[]) => void;
  setLoading: (page: string, isLoading: boolean) => void;
}

export const store = create<AppStoreState>((set, get, api) => ({
  ...createCompaniesSlice(set, get, api),
  ...createUserSlice(set, get, api),
  ...createBankAccountsSlice(set, get, api),
  ...createInstitutionsSlice(set, get, api),
  ...createTransactionsSlice(set, get, api),
  ...createUploadSlice(set, get, api),
  ...createInvoiceSlice(set, get, api),
  ...createStatementsSlice(set, get, api),
}));

export const useAppStore = store;

// Selectors
export const selectUser = (state: AppStoreState) => state.user;
export const selectCompanies = (state: AppStoreState) => state.companies;
export const selectSelectedCompany = (state: AppStoreState) =>
  state.selectedCompany;
export const selectBankAccounts = (state: AppStoreState) => state.bankAccounts;
export const selectInstitutions = (state: AppStoreState) => state.institutions;
